/**
 * Create a P2P fundraiser row and show copyable share URL.
 * One link per campaign per contact — if one already exists, shows it instead of creating a new one.
 */
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Copy } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useCampaigns } from "@/hooks/useCampaigns"
import { getPublicSiteOrigin } from "@/lib/publicSiteUrl"
import { supabase } from "@/lib/supabase"

export interface GenerateFundraiserLinkModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
  org: { slug: string; custom_domain?: string | null }
  defaultCampaignId?: string | null
  defaultName?: string
  defaultEmail?: string
  /** Existing fundraiser links for this contact, keyed by campaign_id. */
  existingLinksByCampaignId?: Record<string, { share_token: string; campaigns: { slug: string } | null }>
  onCreated?: () => void
}

export function GenerateFundraiserLinkModal({
  open,
  onOpenChange,
  orgId,
  org,
  defaultCampaignId = null,
  defaultName = "",
  defaultEmail = "",
  existingLinksByCampaignId = {},
  onCreated,
}: GenerateFundraiserLinkModalProps) {
  const { campaigns, loading: campaignsLoading } = useCampaigns(open ? orgId : null)
  const [name, setName] = useState(defaultName)
  const [email, setEmail] = useState(defaultEmail)
  const [campaignId, setCampaignId] = useState<string>(defaultCampaignId ?? "")
  const [submitting, setSubmitting] = useState(false)
  const [shareUrl, setShareUrl] = useState<string | null>(null)
  const [campaignSlug, setCampaignSlug] = useState<string | null>(null)
  const [isExisting, setIsExisting] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(defaultName)
    setEmail(defaultEmail)
    setShareUrl(null)
    setCampaignSlug(null)
    setIsExisting(false)
    setCampaignId((prev) => defaultCampaignId ?? prev ?? "")
  }, [open, defaultName, defaultEmail, defaultCampaignId])

  useEffect(() => {
    if (open && defaultCampaignId) setCampaignId(defaultCampaignId)
  }, [open, defaultCampaignId])

  function handleCampaignChange(id: string) {
    setCampaignId(id)
    // If a link already exists for this campaign, surface it immediately
    const existing = existingLinksByCampaignId[id]
    if (existing) {
      const camp = campaigns.find((c) => c.id === id)
      const slug = existing.campaigns?.slug ?? camp?.slug ?? ""
      const origin = getPublicSiteOrigin(org)
      setShareUrl(`${origin}/campaign/${slug}/fundraise/${existing.share_token}`)
      setCampaignSlug(slug)
      setIsExisting(true)
    } else {
      setShareUrl(null)
      setCampaignSlug(null)
      setIsExisting(false)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!campaignId || !name.trim()) {
      toast.error("Campaign and name are required")
      return
    }
    const camp = campaigns.find((c) => c.id === campaignId)
    if (!camp) {
      toast.error("Campaign not found")
      return
    }

    setSubmitting(true)
    try {
      let contactId: string | null = null
      if (email.trim()) {
        const { data: existing } = await supabase
          .from("contacts")
          .select("id")
          .eq("org_id", orgId)
          .eq("email", email.trim().toLowerCase())
          .maybeSingle()
        contactId = existing?.id ?? null
      }

      const { data, error } = await supabase
        .from("campaign_fundraisers")
        .insert({
          campaign_id: campaignId,
          org_id: orgId,
          contact_id: contactId,
          name: name.trim(),
        })
        .select("share_token")
        .single()

      if (error) throw error
      const token = data?.share_token as string
      const origin = getPublicSiteOrigin(org)
      const url = `${origin}/campaign/${camp.slug}/fundraise/${token}`
      setShareUrl(url)
      setCampaignSlug(camp.slug)
      setIsExisting(false)
      toast.success("Fundraising link created")
      onCreated?.()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create fundraiser")
    } finally {
      setSubmitting(false)
    }
  }

  async function copyUrl() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success("Copied to clipboard")
    } catch {
      toast.error("Could not copy")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Generate fundraising link</DialogTitle>
          <DialogDescription>
            Creates a peer-to-peer page attributed to this person. One link per campaign.
          </DialogDescription>
        </DialogHeader>
        {shareUrl ? (
          <div className="space-y-3">
            {isExisting && (
              <p className="text-sm text-muted-foreground">
                A link already exists for this campaign.
              </p>
            )}
            <Label>Share URL</Label>
            <div className="flex gap-2">
              <Input readOnly value={shareUrl} className="font-mono text-xs" />
              <Button type="button" size="icon" variant="outline" onClick={() => void copyUrl()}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            {campaignSlug && (
              <p className="text-xs text-muted-foreground">
                Path: /campaign/{campaignSlug}/fundraise/…
              </p>
            )}
            <DialogFooter className="flex gap-2">
              {isExisting && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShareUrl(null)
                    setCampaignId("")
                    setIsExisting(false)
                  }}
                >
                  Different campaign
                </Button>
              )}
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="gf-campaign">Campaign</Label>
              <Select
                value={campaignId || undefined}
                onValueChange={handleCampaignChange}
                disabled={campaignsLoading || campaigns.length === 0}
              >
                <SelectTrigger id="gf-campaign">
                  <SelectValue placeholder="Select campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.title}
                      {existingLinksByCampaignId[c.id] ? " (link exists)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="gf-name">Name (as shown publicly)</Label>
              <Input
                id="gf-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="gf-email">Email (optional — links to contact if found)</Label>
              <Input
                id="gf-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !campaignId}>
                {submitting ? "Creating…" : "Create link"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
